import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { DatabaseService } from '../../core/services/database.service';
import { LoggerService } from '../../core/services/logger.service';

interface TestResult {
  step: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
  error?: string;
}

@Component({
  selector: 'app-db-test',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './db-test.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './db-test.component.css',
})
export class DbTestComponent implements OnInit {
  results = signal<TestResult[]>([]);
  isRunning = signal(false);
  isResetting = signal(false);
  private log = inject(LoggerService).getLogger('db-test');

  constructor(private db: DatabaseService) {}

  ngOnInit(): void {
    // Prevent any automatic database initialization
    // This component explicitly controls all database operations
    this.log.debug('[DB-TEST] Component initialized - database will only init on button click');
  }

  async resetDatabase(): Promise<void> {
    this.isResetting.set(true);
    const results: TestResult[] = [];

    results.push({ step: 'Reset Database', status: 'pending' });
    this.results.set([...results]);

    try {
      await this.db.deleteCorruptedDatabase();
      results[0].status = 'success';
      results[0].message = 'Database deleted successfully - ready for fresh test';
      this.results.set([...results]);
    } catch (error) {
      results[0].status = 'error';
      results[0].error = error instanceof Error ? error.message : String(error);
      this.results.set([...results]);
    } finally {
      this.isResetting.set(false);
    }
  }

  async runTest(): Promise<void> {
    this.isRunning.set(true);
    const results: TestResult[] = [];

    // Step 1: Initialize database
    results.push({ step: 'Initialize Database', status: 'pending' });
    this.results.set([...results]);

    try {
      await this.db.init();
      results[0].status = 'success';
      results[0].message = 'Database initialized successfully';
      this.results.set([...results]);
    } catch (error) {
      results[0].status = 'error';
      results[0].error = error instanceof Error ? error.message : String(error);
      this.results.set([...results]);
      this.isRunning.set(false);
      return; // Stop on initialization failure
    }

    // Step 2: Create test table
    results.push({ step: 'Create Test Table', status: 'pending' });
    this.results.set([...results]);

    try {
      await this.db.exec(`
        DROP TABLE IF EXISTS _smoke_test;
        CREATE TABLE _smoke_test (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          message TEXT NOT NULL,
          created_at INTEGER NOT NULL
        );
      `);
      results[1].status = 'success';
      results[1].message = 'Test table created';
      this.results.set([...results]);
    } catch (error) {
      results[1].status = 'error';
      results[1].error = error instanceof Error ? error.message : String(error);
      this.results.set([...results]);
      this.isRunning.set(false);
      return;
    }

    // Step 3: Insert test data
    results.push({ step: 'Insert Test Data', status: 'pending' });
    this.results.set([...results]);

    try {
      await this.db.run('INSERT INTO _smoke_test (message, created_at) VALUES (?, ?)', [
        'Hello, Database!',
        Date.now(),
      ]);
      results[2].status = 'success';
      results[2].message = 'Test row inserted';
      this.results.set([...results]);
    } catch (error) {
      results[2].status = 'error';
      results[2].error = error instanceof Error ? error.message : String(error);
      this.results.set([...results]);
      this.isRunning.set(false);
      return;
    }

    // Step 4: Read test data
    results.push({ step: 'Read Test Data', status: 'pending' });
    this.results.set([...results]);

    try {
      const row = await this.db.get<{ id: number; message: string; created_at: number }>(
        'SELECT * FROM _smoke_test LIMIT 1',
      );

      if (!row) {
        throw new Error('No data returned from SELECT query');
      }

      if (row.message !== 'Hello, Database!') {
        throw new Error(`Unexpected data: ${row.message}`);
      }

      results[3].status = 'success';
      results[3].message = `Retrieved: "${row.message}" (id: ${row.id})`;
      this.results.set([...results]);
    } catch (error) {
      results[3].status = 'error';
      results[3].error = error instanceof Error ? error.message : String(error);
      this.results.set([...results]);
      this.isRunning.set(false);
      return;
    }

    // Step 5: Clean up
    results.push({ step: 'Clean Up', status: 'pending' });
    this.results.set([...results]);

    try {
      await this.db.exec('DROP TABLE _smoke_test');
      results[4].status = 'success';
      results[4].message = 'Test table dropped';
      this.results.set([...results]);
    } catch (error) {
      results[4].status = 'error';
      results[4].error = error instanceof Error ? error.message : String(error);
      this.results.set([...results]);
    }

    this.isRunning.set(false);
  }

  getStatusIcon(status: 'pending' | 'success' | 'error'): string {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
    }
  }
}
